'use client';

import * as React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Calendar } from './calendar';

interface DatePickerProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  className?: string;
}

export function DatePicker({ value, onChange, className }: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(
    value ? new Date(value) : undefined
  );

  React.useEffect(() => {
    setInternalDate(value ? new Date(value) : undefined);
  }, [value]);

  const handleSelect = (date: Date | undefined) => {
    setInternalDate(date);
    onChange?.(date);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'justify-between font-normal',
            !internalDate && 'text-muted-foreground',
            className || 'w-full'
          )}
          id="date"
          variant="outline"
        >
          {internalDate ? internalDate.toLocaleDateString() : 'Select date'}
          <ChevronDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto overflow-hidden p-0 !bg-[#262626]"
      >
        <Calendar
          captionLayout="dropdown"
          className="!bg-[#262626] [&_select]:!bg-[#262626] [&_select]:!text-white [&_select]:!border-gray-600"
          mode="single"
          selected={internalDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}
